import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  Megaphone, Plus, Send, BarChart3, Trash2, Eye, Users, Search,
  CheckCircle2, XCircle, Clock, ArrowLeft, Mail, MousePointerClick,
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp,
  Paperclip, X, FileText,
} from 'lucide-react';
import EmailEditor from '../components/EmailEditor';
import { generateBrandedEmail } from '../lib/emailService';
import {
  fetchCampaigns, createCampaign, deleteCampaign,
  addRecipientsFromClients, fetchCampaignRecipients, sendCampaign,
  fetchCampaignClicks,
} from '../lib/campaignService';

// ─── HELPERS ───
const pct = (num, den) => den > 0 ? ((num / den) * 100).toFixed(2) + '%' : '0%';
const fmtNum = (n) => (n ?? 0).toLocaleString('en-IE');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS_STYLES = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
  sending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: RefreshCw },
  sent: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
  failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
};

// Default template removed — using EmailEditor + branded wrapper from emailService.js

// ════════════════════════════════════════════════════════════════════════
// MAIN MARKETING PAGE
// ════════════════════════════════════════════════════════════════════════
export default function Marketing() {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'analytics'
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleViewAnalytics = (campaign) => {
    setSelectedCampaign(campaign);
    setView('analytics');
  };

  const handleBack = () => {
    setView('list');
    setSelectedCampaign(null);
    loadCampaigns();
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {view === 'list' && (
        <CampaignList
          campaigns={campaigns}
          loading={loading}
          error={error}
          onCreateNew={() => setView('create')}
          onViewAnalytics={handleViewAnalytics}
          onRefresh={loadCampaigns}
          onDelete={async (id) => {
            await deleteCampaign(id);
            loadCampaigns();
          }}
        />
      )}
      {view === 'create' && (
        <CampaignCreator
          onBack={handleBack}
          onCreated={(campaign) => {
            handleViewAnalytics(campaign);
          }}
        />
      )}
      {view === 'analytics' && selectedCampaign && (
        <CampaignAnalytics
          campaign={selectedCampaign}
          onBack={handleBack}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CAMPAIGN LIST VIEW
// ════════════════════════════════════════════════════════════════════════
function CampaignList({ campaigns, loading, error, onCreateNew, onViewAnalytics, onRefresh, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cloneMode, setCloneMode] = useState(false);
  const [cloneCampaign, setCloneCampaign] = useState(null);

  const sentCampaigns = campaigns.filter(c => c.status === 'sent');
  const totalSent = sentCampaigns.reduce((s, c) => s + (c.total_sent || 0), 0);
  const totalOpened = sentCampaigns.reduce((s, c) => s + (c.total_opened || 0), 0);
  const totalClicked = sentCampaigns.reduce((s, c) => s + (c.total_clicked || 0), 0);

  // If in clone mode, show CampaignCreator with pre-filled data
  if (cloneMode && cloneCampaign) {
    return (
      <CampaignCreator
        onBack={() => {
          setCloneMode(false);
          setCloneCampaign(null);
          onRefresh();
        }}
        cloneData={{
          name: `${cloneCampaign.name} (Copy)`,
          subject: cloneCampaign.subject,
          htmlContent: cloneCampaign.html_content,
          textContent: cloneCampaign.text_content,
        }}
        onCreated={() => {
          setCloneMode(false);
          setCloneCampaign(null);
          onRefresh();
        }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={22} className="text-emerald-600" />
            Email Marketing
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create and track bulk email campaigns to your clients</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onRefresh} className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={onCreateNew} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition flex items-center gap-1.5">
            <Plus size={14} /> New Campaign
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Total Campaigns" value={campaigns.length} icon={Megaphone} color="gray" />
        <SummaryCard label="Emails Sent" value={fmtNum(totalSent)} icon={Send} color="emerald" />
        <SummaryCard label="Avg. Open Rate" value={pct(totalOpened, totalSent)} icon={Eye} color="blue" />
        <SummaryCard label="Avg. Click Rate" value={pct(totalClicked, totalSent)} icon={MousePointerClick} color="purple" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Campaign Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
          <p>Loading campaigns...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border">
          <Megaphone size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No campaigns yet</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">Create your first email campaign to get started</p>
          <button onClick={onCreateNew} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition">
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Campaign</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Sent</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Opened</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Clicked</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Bounced</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const style = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
                  const StatusIcon = style.icon;
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50 transition cursor-pointer" onClick={() => onViewAnalytics(c)}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[250px]">{c.subject}</p>
                        {c.from_email && (
                          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <Mail size={10} /> {c.from_email}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                          <StatusIcon size={12} /> {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{fmtNum(c.total_sent)}</td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">
                        <span className="font-medium">{fmtNum(c.total_opened)}</span>
                        <span className="text-gray-400 ml-1 text-xs">{pct(c.total_opened, c.total_sent)}</span>
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">
                        <span className="font-medium">{fmtNum(c.total_clicked)}</span>
                        <span className="text-gray-400 ml-1 text-xs">{pct(c.total_clicked, c.total_sent)}</span>
                      </td>
                      <td className="px-4 py-3 text-right hidden lg:table-cell text-red-600">{fmtNum(c.total_bounced)}</td>
                      <td className="px-4 py-3 text-right text-gray-500 text-xs">{fmtDate(c.sent_at || c.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {c.status === 'sent' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCloneCampaign(c);
                                setCloneMode(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-500 transition"
                              title="Clone this campaign"
                            >
                              <FileText size={14} />
                            </button>
                          )}
                          {c.status === 'draft' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(c.id); }}
                              className="p-1.5 text-gray-400 hover:text-red-500 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-gray-900 mb-2">Delete Campaign?</h3>
            <p className="text-sm text-gray-500 mb-4">This will permanently delete this draft campaign.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => { onDelete(confirmDelete); setConfirmDelete(null); }} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CAMPAIGN CREATOR
// ════════════════════════════════════════════════════════════════════════
function CampaignCreator({ onBack, onCreated, cloneData }) {
  const { clients } = useAppContext();
  const [step, setStep] = useState(cloneData ? 2 : 1); // 1: recipients, 2: content, 3: review & send
  const [name, setName] = useState(cloneData?.name || '');
  const [subject, setSubject] = useState(cloneData?.subject || '');
  const [bodyContent, setBodyContent] = useState(cloneData?.htmlContent || '');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const isClone = !!cloneData;

  // Filter clients with valid email
  const emailClients = useMemo(() =>
    (clients || []).filter(c => c.email && c.email.trim()),
    [clients]
  );

  const filteredClients = useMemo(() => {
    let result = emailClients;
    if (tagFilter !== 'all') result = result.filter(c => c.tag === tagFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(term) ||
        (c.company && c.company.toLowerCase().includes(term)) ||
        c.email.toLowerCase().includes(term)
      );
    }
    return result;
  }, [emailClients, tagFilter, searchTerm]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const toggleClient = (id) => {
    setSelectedClients(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectedClientObjects = useMemo(() =>
    emailClients.filter(c => selectedClients.includes(c.id)),
    [emailClients, selectedClients]
  );

  // File attachment handling
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB per file

    files.forEach((file) => {
      if (file.size > maxSize) {
        setError(`${file.name} is too large (max 10MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments((prev) => [
          ...prev,
          { filename: file.name, content: base64, contentType: file.type, size: file.size },
        ]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Build full branded HTML from the editor body content
  const buildFullHtml = () => {
    return generateBrandedEmail(bodyContent, subject);
  };

  const handleSend = async () => {
    if (!name.trim() || !subject.trim() || !bodyContent.trim()) {
      setError('Please fill in campaign name, subject, and content');
      return;
    }
    if (selectedClients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    setSending(true);
    setError(null);
    try {
      // 1. Create campaign
      const fullHtml = buildFullHtml();
      const campaign = await createCampaign({
        name: name.trim(),
        subject: subject.trim(),
        htmlContent: fullHtml,
        targetTags: tagFilter !== 'all' ? [tagFilter] : [],
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // 2. Add recipients
      await addRecipientsFromClients(campaign.id, selectedClientObjects);

      // 3. Send (with attachments if any)
      await sendCampaign(campaign.id, attachments);

      onCreated(campaign);
    } catch (err) {
      setError(err.message);
      setSending(false);
    }
  };

  const tagColors = {
    corporate: 'bg-blue-100 text-blue-800',
    wedding: 'bg-pink-100 text-pink-800',
    private: 'bg-purple-100 text-purple-800',
    community: 'bg-green-100 text-green-800',
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isClone ? 'Clone Campaign' : 'New Campaign'}</h1>
          <p className="text-sm text-gray-500">Step {step} of 3</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2 mb-6">
        {[
          { n: 1, label: 'Recipients' },
          { n: 2, label: 'Content' },
          { n: 3, label: 'Review & Send' },
        ]
          .filter(s => isClone ? s.n !== 1 : true)
          .map((s) => (
            <button
              key={s.n}
              onClick={() => setStep(s.n)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                step === s.n
                  ? 'bg-emerald-600 text-white'
                  : step > s.n
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {s.label}
            </button>
          ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* STEP 1: Select Recipients */}
      {step === 1 && !isClone && (
        <div className="bg-white rounded-xl border p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Users size={18} className="text-emerald-600" />
              Select Recipients
              <span className="text-sm font-normal text-gray-400">({selectedClients.length} selected)</span>
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="all">All Tags</option>
              <option value="corporate">Corporate</option>
              <option value="wedding">Wedding</option>
              <option value="private">Private</option>
              <option value="community">Community</option>
            </select>
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition"
            >
              {selectAll ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Client list */}
          <div className="max-h-[400px] overflow-y-auto border rounded-lg">
            {filteredClients.length === 0 ? (
              <p className="text-center py-8 text-gray-400 text-sm">No clients with email addresses found</p>
            ) : (
              filteredClients.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-gray-50 transition ${
                    selectedClients.includes(c.id) ? 'bg-emerald-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(c.id)}
                    onChange={() => toggleClient(c.id)}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{c.name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</p>
                  </div>
                  {c.tag && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${tagColors[c.tag] || 'bg-gray-100 text-gray-600'}`}>
                      {c.tag}
                    </span>
                  )}
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={() => setStep(2)}
              disabled={selectedClients.length === 0}
              className="px-6 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition disabled:opacity-40"
            >
              Next: Content
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Email Content */}
      {step === 2 && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail size={18} className="text-emerald-600" />
            Campaign Content
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name (internal)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Christmas Party 2026 Promo"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Celebrate Christmas at Errigal Country House Hotel"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Content</label>
              <p className="text-xs text-gray-400 mb-2">Use the toolbar to format your email — bold, italic, alignment, links, colours, and more. The Errigal branding (header & footer) is added automatically.</p>
              <EmailEditor
                value={bodyContent}
                onChange={setBodyContent}
                minHeight={280}
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition"
              >
                <Paperclip size={15} />
                Add Attachments
              </button>
              <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Images — max 10MB each</p>

              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                      <FileText size={16} className="text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{att.filename}</p>
                        <p className="text-xs text-gray-400">{formatFileSize(att.size)}</p>
                      </div>
                      <button
                        onClick={() => removeAttachment(i)}
                        className="p-1 text-gray-400 hover:text-red-500 transition"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">
                    {attachments.length} file{attachments.length !== 1 ? 's' : ''} · {formatFileSize(attachments.reduce((s, a) => s + a.size, 0))} total
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between mt-4">
            <button
              onClick={() => isClone ? onBack() : setStep(1)}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!name.trim() || !subject.trim()}
              className="px-6 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition disabled:opacity-40"
            >
              Next: Review
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Review & Send */}
      {step === 3 && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Send size={18} className="text-emerald-600" />
            Review & Send
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Campaign Name</p>
                <p className="font-medium text-gray-900">{name}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Subject Line</p>
                <p className="font-medium text-gray-900">{subject}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Recipients</p>
                <p className="font-medium text-gray-900">{selectedClients.length} clients</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">From</p>
                <p className="font-medium text-gray-900">Errigal Country House Hotel</p>
                <p className="text-xs text-gray-400">events@errigalhotel.com</p>
              </div>
            </div>

            {/* Attachments summary */}
            {attachments.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-2">Attachments ({attachments.length})</p>
                <div className="space-y-1">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <FileText size={14} className="text-gray-400" />
                      <span className="text-gray-700">{att.filename}</span>
                      <span className="text-gray-400 text-xs">({formatFileSize(att.size)})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipient selection (for clones, step must include this) or preview */}
            {isClone && selectedClients.length === 0 ? (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Select Recipients for this campaign</p>
                <p className="text-xs text-gray-500 mb-3">Choose clients to send this cloned campaign to</p>
                <div className="max-h-[200px] overflow-y-auto border rounded-lg bg-gray-50">
                  {filteredClients.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500 text-center">No clients available</div>
                  ) : (
                    filteredClients.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 px-4 py-2 border-b last:border-0 text-sm hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(c.id)}
                          onChange={() => toggleClient(c.id)}
                          className="rounded border-gray-300"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">{c.name}</span>
                          <span className="text-gray-400 ml-2">{c.email}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Recipients ({selectedClientObjects.length})</p>
                <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                  {selectedClientObjects.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-2 border-b last:border-0 text-sm">
                      <span className="font-medium text-gray-900">{c.name}</span>
                      <span className="text-gray-400">{c.email}</span>
                      {c.tag && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${tagColors[c.tag] || 'bg-gray-100'}`}>
                          {c.tag}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <p>This will send {selectedClients.length} emails immediately via your SMTP server. Unsubscribed addresses will be automatically excluded. This action cannot be undone.</p>
            </div>
          </div>

          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(2)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition">
              Back
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-6 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition disabled:opacity-60 flex items-center gap-2"
            >
              {sending ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send size={14} /> Send Campaign
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CAMPAIGN ANALYTICS VIEW
// ════════════════════════════════════════════════════════════════════════
function CampaignAnalytics({ campaign: initialCampaign, onBack }) {
  const [campaign, setCampaign] = useState(initialCampaign);
  const [recipients, setRecipients] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecipients, setShowRecipients] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [recs, clickData] = await Promise.all([
          fetchCampaignRecipients(campaign.id),
          fetchCampaignClicks(campaign.id),
        ]);
        setRecipients(recs);
        setClicks(clickData);

        // Refresh campaign stats
        const fresh = await fetchCampaigns();
        const updated = fresh.find(c => c.id === campaign.id);
        if (updated) setCampaign(updated);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [campaign.id]);

  // Calculate stats from recipients (more accurate than denormalized)
  const stats = useMemo(() => {
    const total = recipients.length;
    const sent = recipients.filter(r => r.status !== 'pending').length;
    const delivered = recipients.filter(r => !['pending', 'bounced', 'failed'].includes(r.status)).length;
    const opened = recipients.filter(r => r.opened_at).length;
    const clicked = recipients.filter(r => r.clicked_at).length;
    const bounced = recipients.filter(r => r.status === 'bounced').length;
    const failed = recipients.filter(r => r.status === 'failed').length;

    return { total, sent, delivered, opened, clicked, bounced, failed };
  }, [recipients]);

  // Top clicked URLs
  const topUrls = useMemo(() => {
    const urlCounts = {};
    clicks.forEach(c => {
      urlCounts[c.url] = (urlCounts[c.url] || 0) + 1;
    });
    return Object.entries(urlCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [clicks]);

  const filteredRecipients = useMemo(() => {
    if (recipientFilter === 'all') return recipients;
    if (recipientFilter === 'opened') return recipients.filter(r => r.opened_at);
    if (recipientFilter === 'clicked') return recipients.filter(r => r.clicked_at);
    if (recipientFilter === 'bounced') return recipients.filter(r => r.status === 'bounced');
    if (recipientFilter === 'unopened') return recipients.filter(r => !r.opened_at && r.status === 'sent');
    return recipients;
  }, [recipients, recipientFilter]);

  if (loading) {
    return (
      <div className="text-center py-16 text-gray-400">
        <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 size={20} className="text-emerald-600" />
            {campaign.name}
          </h1>
          <p className="text-sm text-gray-500">{campaign.subject} · Sent {fmtDate(campaign.sent_at)}</p>
        </div>
        <button
          onClick={() => setShowEmailPreview(!showEmailPreview)}
          className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5"
          title="Preview the sent email content"
        >
          <Eye size={14} /> View Email
        </button>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${(STATUS_STYLES[campaign.status] || STATUS_STYLES.draft).bg} ${(STATUS_STYLES[campaign.status] || STATUS_STYLES.draft).text}`}>
          {campaign.status}
        </span>
      </div>

      {/* Email Preview Panel */}
      {showEmailPreview && campaign.html_content && (
        <div className="bg-white rounded-xl border p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Mail size={16} className="text-emerald-600" />
              Email Preview
            </h3>
            <button
              onClick={() => setShowEmailPreview(false)}
              className="p-1 text-gray-400 hover:text-gray-600 transition"
            >
              <X size={18} />
            </button>
          </div>
          <div className="border rounded-lg bg-gray-50 overflow-hidden">
            <div
              className="max-h-96 overflow-y-auto bg-white"
              dangerouslySetInnerHTML={{ __html: campaign.html_content }}
            />
          </div>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} color="gray" />
        <StatCard label="Sent" value={stats.sent} color="blue" />
        <StatCard label="Delivered" value={stats.delivered} sub={pct(stats.delivered, stats.sent)} color="emerald" />
        <StatCard label="Opened" value={stats.opened} sub={pct(stats.opened, stats.delivered)} color="purple" />
        <StatCard label="Clicked" value={stats.clicked} sub={pct(stats.clicked, stats.delivered)} color="indigo" />
        <StatCard label="Bounced" value={stats.bounced} sub={pct(stats.bounced, stats.sent)} color="red" />
        <StatCard label="Failed" value={stats.failed} color="orange" />
      </div>

      {/* Visual funnel */}
      <div className="bg-white rounded-xl border p-5 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Delivery Funnel</h3>
        <div className="space-y-3">
          <FunnelBar label="Sent" value={stats.sent} max={stats.total} color="bg-blue-500" />
          <FunnelBar label="Delivered" value={stats.delivered} max={stats.total} color="bg-emerald-500" />
          <FunnelBar label="Opened" value={stats.opened} max={stats.total} color="bg-purple-500" />
          <FunnelBar label="Clicked" value={stats.clicked} max={stats.total} color="bg-indigo-500" />
        </div>
      </div>

      {/* Top Links */}
      {topUrls.length > 0 && (
        <div className="bg-white rounded-xl border p-5 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MousePointerClick size={16} className="text-indigo-500" />
            Top Clicked Links
          </h3>
          <div className="space-y-2">
            {topUrls.map(([url, count], i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="text-gray-400 w-6 text-right">{count}</span>
                <div className="flex-1 min-w-0">
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate block">
                    {url}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recipients list */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <button
          onClick={() => setShowRecipients(!showRecipients)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition"
        >
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users size={16} className="text-emerald-600" />
            Recipients ({recipients.length})
          </h3>
          {showRecipients ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showRecipients && (
          <div className="border-t">
            {/* Filter bar */}
            <div className="flex gap-2 px-5 py-3 bg-gray-50 overflow-x-auto">
              {[
                { value: 'all', label: 'All' },
                { value: 'opened', label: `Opened (${stats.opened})` },
                { value: 'clicked', label: `Clicked (${stats.clicked})` },
                { value: 'unopened', label: `Unopened` },
                { value: 'bounced', label: `Bounced (${stats.bounced})` },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setRecipientFilter(f.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${
                    recipientFilter === f.value
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {filteredRecipients.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3 border-b last:border-0 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{r.recipient_name}</p>
                    <p className="text-xs text-gray-400">{r.recipient_email}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {r.opened_at && (
                      <span className="flex items-center gap-1 text-purple-600" title={`Opened ${fmtDateTime(r.opened_at)}`}>
                        <Eye size={12} /> {r.open_count || 1}x
                      </span>
                    )}
                    {r.clicked_at && (
                      <span className="flex items-center gap-1 text-indigo-600" title={`Clicked ${fmtDateTime(r.clicked_at)}`}>
                        <MousePointerClick size={12} /> {r.click_count || 1}x
                      </span>
                    )}
                    {r.status === 'bounced' && (
                      <span className="flex items-center gap-1 text-red-500" title={r.bounce_reason}>
                        <XCircle size={12} /> Bounced
                      </span>
                    )}
                    <RecipientStatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ════════════════════════════════════════════════════════════════════════

function SummaryCard({ label, value, icon: Icon, color }) {
  const colors = {
    gray: 'bg-gray-50 text-gray-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-lg ${colors[color]}`}>
          <Icon size={14} />
        </div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  const colors = {
    gray: 'border-l-gray-400',
    blue: 'border-l-blue-500',
    emerald: 'border-l-emerald-500',
    purple: 'border-l-purple-500',
    indigo: 'border-l-indigo-500',
    red: 'border-l-red-500',
    orange: 'border-l-orange-500',
  };
  return (
    <div className={`bg-white rounded-lg border border-l-4 ${colors[color]} p-3`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900">{fmtNum(value)}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function FunnelBar({ label, value, max, color }) {
  const width = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-20">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500 flex items-center px-2`}
          style={{ width: `${Math.max(width, 2)}%` }}
        >
          {width > 10 && <span className="text-xs text-white font-medium">{fmtNum(value)}</span>}
        </div>
      </div>
      <span className="text-sm font-medium text-gray-700 w-16 text-right">{pct(value, max)}</span>
    </div>
  );
}

function RecipientStatusBadge({ status }) {
  const styles = {
    pending: 'bg-gray-100 text-gray-600',
    sent: 'bg-blue-100 text-blue-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    opened: 'bg-purple-100 text-purple-700',
    clicked: 'bg-indigo-100 text-indigo-700',
    bounced: 'bg-red-100 text-red-700',
    failed: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}
